import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Sparkles } from '../components/Icons.jsx';
import kunalPhoto from '../assets/kunal.png';
import kunalSig from '../assets/kunal-sig.png';

export default function About() {
  return (
    <div style={{ margin: 'calc(-1 * var(--main-py, 0px)) 0 0' }}>
      <div style={pageBg}>
        <div style={pageInner}>

          <div style={kicker}>A letter from</div>
          <h1 style={nameStyle}>Dr Kunal</h1>
          <div style={subhead}>Founder, Medic Mind · Scored 100% in UCAT · 60,000+ applicants mentored to doctors in 10 years</div>

          <div style={polaroidWrap} className="kunal-polaroid">
            <div style={polaroid}>
              <img src={kunalPhoto} alt="Dr Kunal" style={polaroidImg} />
              <div style={polaroidCaption}>Dr Kunal</div>
            </div>
          </div>

          <article style={letter}>
            <p style={firstPara}>
              <span style={dropCap}>W</span>hen I was preparing for the UCAT, I nearly gave up.
            </p>

            <p>I'd done well at A-level. I had work experience lined up. But the UCAT — and Verbal Reasoning especially — was a different problem. I read slowly. I overthought. I'd run out of time on every mock and end up guessing the last passage.</p>

            <p>What changed wasn't talent. It was the <em>process</em>. I worked out how to train the specific reflexes the test rewards — scanning, chunking, the inference rules in VR, the laminate-Venn shortcut in DM, weighted means in QR — and ran them on a schedule that compounded.</p>

            <p>By exam day I scored the <strong>maximum in VR, QR and DM</strong>.</p>

            <aside style={pullQuote}>
              "The gap between a UCAT score that gets interviews and one that doesn't is almost never raw ability. It's whether you trained the right reflexes, in the right order, for long enough."
            </aside>

            <p>That experience became Medic Mind.</p>

            <p>Ten years ago I started by teaching one student over coffee. Today, more than <strong>60,000 students</strong> have prepared with Medic Mind tutors and resources, and gone on to become doctors and dentists — more than any other UK medical-applications tutoring company in the same period.</p>

            <p>The lesson from teaching tens of thousands of applicants is consistent. The gap is almost never raw ability. It's whether you trained the right reflexes, in the right order, for long enough.</p>

            <p>Over the years, I've personally trained every Medic Mind tutor on these exact playbooks. Same drills, same shortcuts, same rules. Their students' average UCAT lands in the <strong>top 30% nationally</strong> — well above the median. The portal you're using is a free version of what those tutors teach in 1-to-1 sessions.</p>

            <p style={{ ...para, fontWeight: 600 }}>That's why I built Medi.</p>

            <p>This whole portal — the 13 drills, the personal-statement analyser, Ash the AI tutor, the Career Camp pages, the Plan and Apply hubs — exists because most students need the same things and have to piece them together from a dozen tabs. I wanted to put them in one place, free, designed by someone who's actually sat the UCAT, sat the interview, taught the section, and watched what works for tens of thousands of applicants.</p>

            <p>If you're stuck, or panicked, or thinking you're not smart enough — you almost certainly are. You just need the process. That's what we've built here.</p>

            <p style={{ marginTop: 32 }}>I hope it helps,</p>

            <img src={kunalSig} alt="Dr Kunal signature" style={signatureImg} />

            <div style={signoff}>
              <strong style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 15, color: 'var(--text)' }}>Dr Kunal</strong>
              <div style={{ color: 'var(--text-muted)', fontSize: 13, fontFamily: 'Montserrat, sans-serif', marginTop: 2 }}>Founder, Medic Mind</div>
            </div>

            <div style={postscript}>
              <strong>P.S.</strong> If you want a human walking through this with you, every Medic Mind tutor was personally trained by me on the same playbooks the portal teaches. Free 15-min advisor call, or jump straight to 1-to-1 tutoring. No script, no pitch.
            </div>
          </article>

          <div style={receipts}>
            <Stat value="60,000+" label="applicants mentored to doctors over 10 years" />
            <Stat value="100%" label="UCAT scored by Dr Kunal — playbooks baked into the drills" />
            <Stat value="Top 30%" label="average UCAT score of Medic Mind tutees — above the national average" />
            <Stat value="Every tutor" label="personally trained by Dr Kunal on these playbooks" />
          </div>

          <section style={ctaWrap}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <h2 style={{ margin: '0 0 4px', fontSize: 20, fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>Want a tutor I trained?</h2>
              <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 14.5 }}>Free 15-min advisor call to scope it, or jump straight to 1-to-1 tutoring. Same playbooks as the portal — taught by a human.</p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Link to="/tutor-match" className="btn btn-primary"><Sparkles width="14" height="14" />See tutoring <ArrowRight width="14" height="14" /></Link>
              <a className="btn btn-secondary"><Phone width="14" height="14" />Free 15-min call</a>
            </div>
          </section>

          <div style={{ height: 24 }} />
        </div>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .kunal-polaroid { float: none !important; margin: 0 auto 22px !important; display: block; }
        }
      `}</style>
    </div>
  );
}

const pageBg = {
  background: '#fdf8ef',
  backgroundImage: 'radial-gradient(circle at 12% 18%, rgba(124, 62, 175, 0.07), transparent 40%), radial-gradient(circle at 86% 90%, rgba(58, 184, 166, 0.08), transparent 50%)',
  margin: '-28px -36px -80px',
  padding: '0 0 60px'
};

const pageInner = {
  maxWidth: 760, margin: '0 auto',
  padding: 'clamp(28px, 5vw, 56px) clamp(20px, 4vw, 48px) 0'
};

const kicker = {
  fontSize: 11.5, letterSpacing: '0.18em', textTransform: 'uppercase',
  color: 'var(--brand-purple)', fontWeight: 700,
  fontFamily: 'Montserrat, sans-serif'
};

const nameStyle = {
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontSize: 'clamp(36px, 7vw, 56px)',
  margin: '6px 0 8px',
  fontWeight: 400, fontStyle: 'italic',
  color: 'var(--brand-deep)',
  letterSpacing: '-0.01em',
  lineHeight: 1.05
};

const subhead = {
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 14,
  color: 'var(--text-muted)',
  marginBottom: 28,
  letterSpacing: '0.02em'
};

const polaroidWrap = {
  float: 'right',
  margin: '4px -10px 18px 28px'
};

const polaroid = {
  background: '#fff',
  padding: '10px 10px 14px',
  border: '1px solid rgba(0,0,0,0.05)',
  boxShadow: '0 18px 40px rgba(35, 20, 63, 0.22), 0 2px 6px rgba(0,0,0,0.06)',
  transform: 'rotate(3deg)',
  transition: 'transform 250ms ease'
};

const polaroidImg = {
  display: 'block',
  width: 200, height: 240,
  objectFit: 'cover',
  filter: 'saturate(0.95)'
};

const polaroidCaption = {
  fontFamily: 'Georgia, serif',
  fontStyle: 'italic',
  fontSize: 13,
  color: 'var(--text-muted)',
  textAlign: 'center',
  marginTop: 8,
  letterSpacing: '0.02em'
};

const letter = {
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontSize: 17.5,
  lineHeight: 1.8,
  color: 'var(--text)',
  overflow: 'auto'
};

const para = { margin: '0 0 18px' };

const firstPara = {
  ...para,
  fontSize: 19.5,
  color: 'var(--brand-deep)'
};

const dropCap = {
  float: 'left',
  fontFamily: 'Georgia, serif',
  fontSize: 78,
  lineHeight: 0.78,
  fontWeight: 700,
  color: 'var(--brand-purple)',
  paddingRight: 10,
  paddingTop: 6,
  marginTop: 0
};

const pullQuote = {
  fontFamily: 'Georgia, serif',
  fontStyle: 'italic',
  fontSize: 19,
  lineHeight: 1.55,
  color: 'var(--brand-deep)',
  borderLeft: '3px solid var(--brand-purple)',
  paddingLeft: 18,
  margin: '20px 0 24px',
  display: 'block'
};

const signatureImg = {
  display: 'block',
  maxWidth: 240,
  height: 'auto',
  marginTop: 6,
  marginBottom: 8,
  marginLeft: -8,
  transform: 'rotate(-2deg)'
};

const signoff = {
  marginTop: 8
};

const postscript = {
  marginTop: 28,
  paddingTop: 18,
  borderTop: '1px dashed rgba(35, 20, 63, 0.15)',
  fontFamily: 'Georgia, serif',
  fontSize: 15,
  color: 'var(--text-2)',
  lineHeight: 1.65,
  fontStyle: 'italic'
};

const receipts = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 14,
  marginTop: 40,
  marginBottom: 26
};

const ctaWrap = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  flexWrap: 'wrap',
  padding: '18px 22px',
  background: '#fff',
  border: '1px solid var(--border)',
  borderRadius: 14,
  boxShadow: 'var(--shadow-card)',
  marginBottom: 20
};

function Stat({ value, label }) {
  return (
    <div style={{ padding: '14px 16px', background: '#fff', border: '1px solid var(--border)', borderRadius: 12, borderLeft: '4px solid var(--brand-purple)' }}>
      <div style={{ fontSize: 'clamp(24px, 3.5vw, 30px)', fontWeight: 800, fontFamily: 'Montserrat, sans-serif', color: 'var(--brand-deep)', letterSpacing: '-0.01em' }}>{value}</div>
      <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-2)' }} dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}
